import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

import { useBreakpoint } from 'js/design_system/hooks/useBreakpoint';
import { Button } from 'js/design_system/Button';
import { trackClick } from 'js/utils/trackClick';
import { openLoginModal } from 'js/login_modal/LoginModalManager';
import { CitationButton } from './CitationButton';

const Container = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: var(--background-white);
  border-top: 1px solid var(--stroke-light, #dddde2);
  padding: var(--spacing-xs-12) var(--spacing-xs-16);
  padding-bottom: calc(var(--spacing-xs-12) + env(safe-area-inset-bottom, 0px));

  &.hidden {
    display: none;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: clamp(8px, calc(17vw - 46px), 24px);
`;

const ButtonItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const Label = styled.span`
  color: var(--text-primary, #141413);
  text-align: center;
  font-family: var(--type-font-family-sans-serif, 'DM Sans');
  font-size: var(--type-body-xs-size, 12px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--type-body-xs-line-height, 16px);
`;

const iconButtonClassName = 'sticky-cta-footer__btn';
const primaryButtonClassName =
  'sticky-cta-footer__btn sticky-cta-footer__btn--primary';

interface Props {
  workId: number;
  workUrl: string;
  workTitle?: string;
  workAuthorName?: string;
  workThumbnailUrl?: string;
  workYear?: number;
  workViewCount?: number;
  attachmentId?: number;
  designVariant: string;
}

export const StickyCtaFooter: React.FC<Props> = ({
  workId,
  workUrl,
  workTitle,
  workAuthorName,
  workThumbnailUrl,
  workYear,
  workViewCount,
  attachmentId,
  designVariant,
}) => {
  const [areActionButtonsVisible, setAreActionButtonsVisible] = useState(true);
  const [isDocumentBottomInView, setIsDocumentBottomInView] = useState(false);
  const breakpoint = useBreakpoint();
  const isDesktop = breakpoint === 'large';

  useEffect(() => {
    const actionButtons = document.querySelector(
      '.ungated-work-card--action-buttons',
    ) as HTMLElement;
    if (!actionButtons) {
      setAreActionButtonsVisible(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setAreActionButtonsVisible(entry.isIntersecting);
      },
      { threshold: 0 },
    );

    observer.observe(actionButtons);
    return () => observer.disconnect();
  }, []);

  // Track when document bottom comes into view
  useEffect(() => {
    const embeddedDocument = document.querySelector(
      '.js-loswp-embedded-document',
    ) as HTMLElement;
    if (!embeddedDocument) return;

    const checkDocumentBottomInView = () => {
      const rect = embeddedDocument.getBoundingClientRect();
      setIsDocumentBottomInView(rect.bottom <= window.innerHeight);
    };

    window.addEventListener('scroll', checkDocumentBottomInView);
    checkDocumentBottomInView();

    return () =>
      window.removeEventListener('scroll', checkDocumentBottomInView);
  }, []);

  const createClickHandler = (
    source: string,
    options?: {
      autoDownload?: boolean;
      modalText?: Record<string, Record<string, string>>;
    },
  ) => {
    return () => {
      const trackingContext = {
        source,
        bucket: designVariant,
        entityId: workId,
        extra: { source_work_id: workId },
      };

      trackClick(trackingContext);

      let redirectUrl = workUrl;
      if (options?.autoDownload) {
        const url = new URL(redirectUrl, window.location.origin);
        url.searchParams.set('auto', 'download');
        redirectUrl = url.toString();
      }

      openLoginModal({
        postLoginRedirectUrl: redirectUrl,
        workTitle,
        authorName: workAuthorName,
        workThumbnailUrl,
        workYear,
        workViewCount,
        clickTrackingContext: trackingContext,
        configuredText: options?.modalText || {
          buttonsPage: { header: 'Read your PDF for free' },
          emailEntryPage: {
            subheader: 'Sign up or log in to continue reading.',
          },
          emailLoginPage: { subheader: 'Log in to continue reading.' },
          emailRegistrationPage: { subheader: 'Sign up to continue reading.' },
        },
        registrationParams: {
          user_source: 'download',
          doc_id: attachmentId,
          ...(options?.autoDownload && { auto: 'download' }),
        },
      });
    };
  };

  const shouldShow =
    !isDesktop && !areActionButtonsVisible && !isDocumentBottomInView;

  // This is so that the floating badges know where to position themselves
  useEffect(() => {
    const floatingBadges = document.querySelector('.js-floating-badges');
    if (floatingBadges) {
      floatingBadges.classList.toggle('footer-visible', shouldShow);
    }
  }, [shouldShow]);

  return (
    <Container className={`js-sticky-cta-footer ${shouldShow ? '' : 'hidden'}`}>
      <ButtonContainer>
        <ButtonItem>
          <Button
            icon="bookmark_border"
            secondary
            size="sm"
            className={iconButtonClassName}
            onClick={createClickHandler('loswp-ungate-sticky-save')}
          />
          <Label>Save</Label>
        </ButtonItem>

        <ButtonItem>
          <Button
            icon="share"
            secondary
            size="sm"
            className={iconButtonClassName}
            onClick={createClickHandler('loswp-ungate-sticky-share')}
          />
          <Label>Share</Label>
        </ButtonItem>

        <ButtonItem>
          <Button
            icon="download"
            size="sm"
            className={primaryButtonClassName}
            onClick={createClickHandler('loswp-ungate-sticky-download', {
              autoDownload: true,
              modalText: {
                buttonsPage: { header: 'Download your PDF for free' },
                emailEntryPage: {
                  subheader:
                    'Sign up or log in to continue with your free download.',
                },
                emailLoginPage: {
                  subheader: 'Log in to continue with your free download.',
                },
                emailRegistrationPage: {
                  subheader: 'Sign up to continue with your free download.',
                  submit: 'Start Download',
                },
              },
            })}
          />
          <Label>Download</Label>
        </ButtonItem>

        <ButtonItem>
          <Button
            icon="description"
            secondary
            size="sm"
            className={iconButtonClassName}
            onClick={createClickHandler('loswp-ungate-sticky-see-pdf')}
          />
          <Label>See PDF</Label>
        </ButtonItem>

        <ButtonItem>
          <CitationButton
            workId={workId}
            source="loswp-ungate-sticky-cite"
            buttonClassName={iconButtonClassName}
          />
          <Label>Cite</Label>
        </ButtonItem>
      </ButtonContainer>
    </Container>
  );
};

export default StickyCtaFooter;
